import React, {ReactElement, useEffect, useState} from 'react';
import {
    Alert,
    AlertType,
    Button,
    ButtonClasses,
    LoadingView,
    UploadField,
} from '@cuatroochenta/co-admin-react-library';
import {Importer, SUMMARY_LABELS} from './importers';
import {downloadFile, errorText, ImportPayload, postFile, postJson, RowErrorPayload} from './importRequest';

type CityOption = {oid: number, name: string};

type PreviewRow = {
    line: number,
    sheet?: string,
    errors: {column: string, message: string}[],
    [key: string]: unknown,
};

type Preview = {valid: boolean, rows: PreviewRow[], summary: Record<string, number>};

const CITY_LIST_PATH = 'city/list/1';
const CITY_LIST_MAX_PER_PAGE = 1000;
const GENERIC_ERROR = 'No se ha podido completar la operación';
const PREVIEW_ROW_KEYS_SHOWN_APART = ['line', 'sheet', 'errors'];

const summaryText = (summary: Record<string, number>): string => Object.entries(summary)
    .map(([key, value]) => `${SUMMARY_LABELS[key] ?? key}: ${value}`)
    .join(' · ');

const recordOf = (row: PreviewRow): string => Object.entries(row)
    .filter(([key]) => !PREVIEW_ROW_KEYS_SHOWN_APART.includes(key))
    .map(([, value]) => String(value))
    .join(' · ');

const failureText = (payload: ImportPayload): string => errorText(payload) || GENERIC_ERROR;

export const ImportScreen = ({importer}: {importer: Importer}): ReactElement => {
    const [cities, setCities] = useState<CityOption[]>([]);
    const [cityId, setCityId] = useState<number | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [rowErrors, setRowErrors] = useState<RowErrorPayload[]>([]);
    const [error, setError] = useState<string>('');
    const [result, setResult] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        postJson({path: CITY_LIST_PATH, body: {maxPerPage: CITY_LIST_MAX_PER_PAGE}})
            .then((payload) => {
                if (!payload.success) {
                    setError(failureText(payload));
                    return;
                }
                const options = (payload.data.results as {oid: number, name?: string, code?: string}[])
                    .map((city) => ({oid: Number(city.oid), name: city.name ?? city.code ?? String(city.oid)}))
                    .sort((left, right) => left.name.localeCompare(right.name));
                setCities(options);
            })
            .catch(() => setError(GENERIC_ERROR));
    }, []);

    const resetOutcome = (): void => {
        setPreview(null);
        setRowErrors([]);
        setError('');
        setResult('');
    };

    const cityPath = (step: string): string => `city/${cityId}/${importer.pathSegment}/${step}`;

    const run = async (work: () => Promise<void>): Promise<void> => {
        setLoading(true);
        try {
            await work();
        } catch {
            setError(GENERIC_ERROR);
        } finally {
            setLoading(false);
        }
    };

    const onDownloadTemplate = (): Promise<void> => run(async () => {
        resetOutcome();
        const payload = await downloadFile({path: cityPath('template'), fileName: importer.templateFileName});
        if (payload !== null) {
            setError(failureText(payload));
        }
    });

    const onPreview = (): Promise<void> => run(async () => {
        if (file === null) {
            return;
        }
        resetOutcome();
        const payload = await postFile({path: cityPath('preview'), file});
        if (!payload.success) {
            setError(failureText(payload));
            return;
        }
        const loadedPreview = payload.data as Preview;
        setPreview(loadedPreview);
        if (!loadedPreview.valid) {
            setFile(null);
        }
    });

    const onApply = (): Promise<void> => run(async () => {
        if (file === null) {
            return;
        }
        setError('');
        setRowErrors([]);
        setResult('');
        const payload = await postFile({path: cityPath('apply'), file});
        if (!payload.success) {
            setError(failureText(payload));
            setRowErrors(payload.errors ?? []);
            if (payload.errors !== undefined) {
                setPreview(null);
            }
            return;
        }
        setPreview(null);
        setResult(`Importación aplicada. ${summaryText(payload.data as Record<string, number>)}`);
    });

    const hasSheets = preview !== null && preview.rows.some((row) => row.sheet !== undefined);

    return (
        <div className='card import-screen' data-testid={`${importer.name}-screen`}>
            <div className='card-body'>
                <LoadingView loading={loading}/>
                <div className='form-group'>
                    <label htmlFor={`${importer.name}-city`}>Ciudad</label>
                    <select
                        id={`${importer.name}-city`}
                        className='form-control'
                        data-testid={`${importer.name}-city-select`}
                        value={cityId ?? ''}
                        disabled={loading}
                        onChange={(event) => {
                            setCityId(event.target.value === '' ? null : Number(event.target.value));
                            resetOutcome();
                        }}
                    >
                        <option value=''>Selecciona una ciudad</option>
                        {cities.map((city) => <option key={city.oid} value={city.oid}>{city.name}</option>)}
                    </select>
                </div>
                <UploadField
                    value=''
                    field={`${importer.name}-file`}
                    name='file'
                    type='FILE'
                    label='Fichero Excel (.xlsx)'
                    isEditScreen={false}
                    disabled={cityId === null || loading}
                    helperText={file?.name ?? ''}
                    removeFile={() => {
                        setFile(null);
                        resetOutcome();
                    }}
                    taskUpload={(event) => {
                        setFile(event.target.files?.[0] ?? null);
                        resetOutcome();
                    }}
                    dataTestId={`${importer.name}-file-input`}
                />
                <div className='import-screen-buttons'>
                    <Button
                        content='Descargar plantilla'
                        buttonClass={ButtonClasses.CUSTOM_BUTTON_BORDER}
                        disabled={cityId === null || loading}
                        onClick={onDownloadTemplate}
                        dataTestId={`${importer.name}-template-button`}
                    />
                    <Button
                        content='Previsualizar'
                        disabled={cityId === null || file === null || loading}
                        onClick={onPreview}
                        dataTestId={`${importer.name}-preview-button`}
                    />
                    <Button
                        content='Aplicar'
                        disabled={preview === null || !preview.valid || loading}
                        onClick={onApply}
                        dataTestId={`${importer.name}-apply-button`}
                    />
                </div>
                <div role='alert' data-testid={`${importer.name}-error`}>
                    <Alert show={error !== ''} message={error} type={AlertType.DANGER}/>
                </div>
                <div role='alert' data-testid={`${importer.name}-result`}>
                    <Alert show={result !== ''} message={result} type={AlertType.SUCCESS}/>
                </div>
                {preview !== null && (
                    <div aria-live='polite' data-testid={`${importer.name}-preview`}>
                        <p data-testid={`${importer.name}-preview-status`}>
                            {preview.valid
                                ? 'El fichero es válido. Revisa el resumen y pulsa «Aplicar».'
                                : 'Corrige los errores, vuelve a seleccionar el fichero y previsualiza'}
                        </p>
                        <p data-testid={`${importer.name}-preview-summary`}>{summaryText(preview.summary)}</p>
                        <table className='table table-sm' data-testid={`${importer.name}-preview-table`}>
                            <thead>
                                <tr>
                                    {hasSheets && <th>Hoja</th>}
                                    <th>Fila</th>
                                    <th>Registro</th>
                                    <th>Errores</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.rows.map((row) => (
                                    <tr
                                        key={`${row.sheet ?? ''}-${row.line}`}
                                        className={row.errors.length > 0 ? 'table-danger' : ''}
                                    >
                                        {hasSheets && <td>{row.sheet}</td>}
                                        <td>{row.line}</td>
                                        <td>{recordOf(row)}</td>
                                        <td>{row.errors.map((rowError) => `${rowError.column}: ${rowError.message}`).join(' · ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {rowErrors.length > 0 && (
                    <table className='table table-sm' data-testid={`${importer.name}-apply-errors`}>
                        <thead>
                            <tr>
                                <th>Fila</th>
                                <th>Columna</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rowErrors.map((rowError, index) => (
                                <tr key={index}>
                                    <td>{rowError.line}</td>
                                    <td>{rowError.column}</td>
                                    <td>{rowError.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
